import React, { useState } from 'react';
import api from '../api/client';
import toast from 'react-hot-toast';
import { Heart, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import { useAuth } from './AuthContext';


const DEVICE_FORMS = {
  blood_pressure: {
    label: 'Blood Pressure',
    icon: '🩺',
    deviceId: 'manual-bp',
    fields: [
      { key: 'systolic', label: 'Systolic', unit: 'mmHg', type: 'number', placeholder: '120', min: 50, max: 250 },
      { key: 'diastolic', label: 'Diastolic', unit: 'mmHg', type: 'number', placeholder: '80', min: 30, max: 150 },
    ]
  },
  heart_rate: {
    label: 'Heart Rate',
    icon: '💓',
    deviceId: 'manual-hr',
    fields: [
      { key: 'heartRate', label: 'BPM', unit: 'bpm', type: 'number', placeholder: '72', min: 30, max: 250 }
    ]
  },
  pulse_oximeter: {
    label: 'Oxygen Saturation',
    icon: '🫁',
    deviceId: 'manual-spo2',
    fields: [
      { key: 'spo2', label: 'SpO₂', unit: '%', type: 'number', placeholder: '98', min: 50, max: 100 }
    ]
  },
  weight_scale: {
    label: 'Weight',
    icon: '⚖️',
    deviceId: 'manual-weight',
    fields: [
      { key: 'weight', label: 'Weight', unit: 'lbs', type: 'number', placeholder: '150', min: 50, max: 600 },
    ]
  },
  glucometer: {
    label: 'Blood Glucose',
    icon: '🩸',
    deviceId: 'manual-glucose',
    fields: [
      { key: 'glucose', label: 'Glucose', unit: 'mg/dL', type: 'number', placeholder: '100', min: 20, max: 600 }
    ]
  },
  temperature: {
    label: 'Temperature',
    icon: '🌡️',
    deviceId: 'manual-temp',
    fields: [
      { key: 'temperature', label: 'Temp', unit: '°F', type: 'number', placeholder: '98.6', min: 90, max: 110, step: '0.1' }
    ]
  }
};

const VitalsEntryWidget = ({ onSuccess } = {}) => {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('blood_pressure');
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [lastSubmitted, setLastSubmitted] = useState(null);

  const currentForm = DEVICE_FORMS[activeTab];

  const handleSubmit = async () => {
    const measurements = {};
    for (const field of currentForm.fields) {
      const val = parseFloat(values[`${activeTab}:${field.key}`]);
      if (isNaN(val) || val === '') {
        toast.error(`Enter a value for ${field.label}`);
        return;
      }
      if (field.min !== undefined && val < field.min) {
        toast.error(`${field.label} must be at least ${field.min}`);
        return;
      }
      if (field.max !== undefined && val > field.max) {
        toast.error(`${field.label} must be at most ${field.max}`);
        return;
      }
      measurements[field.key] = val;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/integrations/devices/reading', {
        patientId: user.id,
        deviceType: activeTab,
        deviceId: currentForm.deviceId,
        measurements,
        timestamp: new Date().toISOString()
      });

      const severity = res.data?.severity;
      if (severity === 'critical') {
        toast.error('Critical reading detected — your care team has been alerted', { duration: 6000 });
      } else if (severity === 'high') {
        toast('Reading flagged as high — your care team will review', { icon: '⚠️', duration: 4000 });
      } else {
        toast.success(`${currentForm.label} recorded`);
      }

      setLastSubmitted({ type: activeTab, label: currentForm.label, measurements, at: new Date() });
      onSuccess?.();
      // Clear only the current tab's fields
      setValues(v => {
        const next = { ...v };
        for (const field of currentForm.fields) {
          delete next[`${activeTab}:${field.key}`];
        }
        return next;
      });
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error;
      toast.error(msg || 'Failed to submit reading');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section aria-label="Log vitals">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full bg-white rounded-2xl p-4 sm:p-5 border border-outline-variant/10 shadow-sm hover:shadow-md transition-all text-left flex items-center gap-4"
      >
        <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
          <Heart className="w-5 h-5 text-rose-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900">Log Vitals</p>
          <p className="text-xs text-slate-500">
            {lastSubmitted
              ? `Last: ${lastSubmitted.label} at ${lastSubmitted.at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
              : 'Blood pressure, weight, SpO₂ and more'}
          </p>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>

      {expanded && (
        <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm mt-2 overflow-hidden">
          {/* Tab row */}
          <div className="flex overflow-x-auto border-b border-slate-100 px-4 pt-3 gap-1 no-scrollbar">
            {Object.entries(DEVICE_FORMS).map(([key, form]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-t-lg whitespace-nowrap transition-all border-b-2 ${
                  activeTab === key
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <span>{form.icon}</span> {form.label}
              </button>
            ))}
          </div>

          {/* Form */}
          <div className="p-5">
            <div className="grid grid-cols-2 gap-3 mb-4">
              {currentForm.fields.map(field => (
                <div key={field.key} className={currentForm.fields.length === 1 ? 'col-span-2' : ''}>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    {field.label} <span className="text-slate-400 font-normal">({field.unit})</span>
                  </label>
                  <input
                    type="number"
                    placeholder={field.placeholder}
                    step={field.step || '1'}
                    min={field.min}
                    max={field.max}
                    value={values[`${activeTab}:${field.key}`] || ''}
                    onChange={e => setValues(v => ({ ...v, [`${activeTab}:${field.key}`]: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 bg-slate-50"
                  />
                </div>
              ))}
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-3 bg-primary text-white rounded-xl font-bold text-sm hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? 'Submitting...' : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Record {currentForm.label}
                </>
              )}
            </button>

            {lastSubmitted?.type === activeTab && (
              <p className="text-center text-xs text-emerald-600 font-medium mt-2">
                Last recorded at {lastSubmitted.at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default VitalsEntryWidget;
